import React, { Component } from 'react';
import { Card, CardBody, CardHeader, Button, Badge, Spinner, Alert } from 'reactstrap';
import moment from 'moment';
import { getEmailConfiguration, toggleEmailConfiguration } from '../../../services/monitoring.service';
import AuthService from '../../Auth/AuthService';
import PermissionFeature from '../../Permissions/PermissionFeature';

/**
 * EmailConfigPanel Component
 *
 * Displays and controls the global email sending configuration.
 * Allows admins to toggle email sending on/off from the UI.
 *
 * When email is OFF:
 * - Alerts are still created in the database
 * - Email content is logged to EmailLog table
 * - No SMTP emails are sent
 * - Logged emails can be viewed in the dashboard
 */
class EmailConfigPanel extends Component {
    constructor(props) {
        super(props);

        this.state = {
            config: null,
            loading: true,
            error: null,
            updating: false,
            updateMessage: null
        };

        this.refreshInterval = null;
    }

    componentDidMount() {
        this.loadConfig();

        // Refresh config every 30 seconds
        this.refreshInterval = setInterval(() => {
            this.loadConfig(true);
        }, 30000);
    }

    componentWillUnmount() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }
    }

    loadConfig = async (silent = false) => {
        try {
            if (!silent) {
                this.setState({ loading: true, error: null });
            }

            const response = await getEmailConfiguration();

            if (response.status === 'Success' && response.data) {
                this.setState({
                    config: response.data,
                    loading: false,
                    error: null
                });
            } else {
                throw new Error(response.message || 'Failed to load email configuration');
            }
        } catch (error) {
            console.error('Error loading email config:', error);
            this.setState({
                error: error.message || 'Failed to load email configuration',
                loading: false
            });
        }
    };

    handleToggle = async () => {
        const { config } = this.state;
        if (!config) return;

        const newEnabledState = config.emailEnabled === 0 ? true : false;

        this.setState({ updating: true, updateMessage: null });

        try {
            const response = await toggleEmailConfiguration(newEnabledState, 'Admin');

            if (response.status === 'Success') {
                // Reload config to get updated values
                await this.loadConfig(true);

                this.setState({
                    updating: false,
                    updateMessage: {
                        type: 'success',
                        text: response.message || `Email sending ${newEnabledState ? 'enabled' : 'disabled'} successfully`
                    }
                });

                // Clear success message after 5 seconds
                setTimeout(() => {
                    this.setState({ updateMessage: null });
                }, 5000);

                // Notify parent to refresh alerts if provided
                if (this.props.onConfigChange) {
                    this.props.onConfigChange(newEnabledState);
                }
            } else {
                throw new Error(response.message || 'Failed to update email configuration');
            }
        } catch (error) {
            console.error('Error toggling email config:', error);
            this.setState({
                updating: false,
                updateMessage: {
                    type: 'danger',
                    text: error.message || 'Failed to update email configuration'
                }
            });
        }
    };

    render() {
        const { config, loading, error, updating, updateMessage } = this.state;

        // Check if user has permission to manage email configuration
        const canManageEmail = AuthService.canUpdate(PermissionFeature.MonitoringDashboardManageEmail);

        if (loading && !config) {
            return (
                <Card className="mb-3">
                    <CardHeader className="bg-info text-white">
                        <i className="fa fa-envelope mr-2"></i>
                        Email Configuration
                    </CardHeader>
                    <CardBody className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2 mb-0">Loading email configuration...</p>
                    </CardBody>
                </Card>
            );
        }

        if (error && !config) {
            return (
                <Card className="mb-3">
                    <CardHeader className="bg-info text-white">
                        <i className="fa fa-envelope mr-2"></i>
                        Email Configuration
                    </CardHeader>
                    <CardBody>
                        <Alert color="danger">
                            <i className="fa fa-exclamation-triangle mr-2"></i>
                            {error}
                        </Alert>
                        <Button color="primary" size="sm" onClick={() => this.loadConfig()}>
                            <i className="fa fa-refresh mr-1"></i>
                            Retry
                        </Button>
                    </CardBody>
                </Card>
            );
        }

        const isEnabled = config && config.emailEnabled === 1;
        const statusColor = isEnabled ? 'success' : 'warning';
        const statusText = isEnabled ? 'Enabled' : 'Disabled';
        const statusIcon = isEnabled ? 'check-circle' : 'ban';

        return (
            <Card className="mb-3">
                <CardHeader className="bg-info text-white d-flex justify-content-between align-items-center">
                    <span>
                        <i className="fa fa-envelope mr-2"></i>
                        Email Configuration
                    </span>
                    <Badge color={statusColor} pill>
                        <i className={`fa fa-${statusIcon} mr-1`}></i>
                        {statusText}
                    </Badge>
                </CardHeader>
                <CardBody>
                    {updateMessage && (
                        <Alert color={updateMessage.type} className="mb-3">
                            {updateMessage.text}
                        </Alert>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-1">Email Sending Status</h6>
                            <small className="text-muted">
                                {isEnabled ? (
                                    <>
                                        <i className="fa fa-info-circle mr-1"></i>
                                        Emails are being sent via SMTP for all alerts
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-exclamation-triangle mr-1"></i>
                                        Emails are NOT being sent - content is logged to database only
                                    </>
                                )}
                            </small>
                        </div>
                        {canManageEmail ? (
                            <Button
                                color={isEnabled ? 'success' : 'secondary'}
                                onClick={this.handleToggle}
                                disabled={updating}
                                size="sm"
                                style={{ minWidth: '80px' }}
                            >
                                {updating ? (
                                    <>
                                        <Spinner size="sm" className="mr-1" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <i className={`fa fa-${isEnabled ? 'toggle-on' : 'toggle-off'} mr-1`}></i>
                                        {isEnabled ? 'ON' : 'OFF'}
                                    </>
                                )}
                            </Button>
                        ) : (
                            <Badge color={statusColor} pill className="px-3 py-2">
                                <i className={`fa fa-${statusIcon} mr-1`}></i>
                                {statusText}
                            </Badge>
                        )}
                    </div>

                    {!canManageEmail && (
                        <Alert color="secondary" className="mb-3">
                            <small>
                                <i className="fa fa-lock mr-1"></i>
                                <strong>Read-Only:</strong> You don't have permission to modify email configuration.
                                Contact your system administrator to enable/disable email sending.
                            </small>
                        </Alert>
                    )}

                    <hr />

                    <div className="small text-muted">
                        <div className="mb-1">
                            <strong>Last Updated:</strong>{' '}
                            {config.lastUpdated
                                ? moment.utc(config.lastUpdated).local().format('MMM D, YYYY h:mm:ss A')
                                : 'N/A'}
                        </div>
                        <div>
                            <strong>Updated By:</strong> {config.updatedBy || 'System'}
                        </div>
                    </div>

                    {!isEnabled && (
                        <Alert color="info" className="mt-3 mb-0">
                            <small>
                                <i className="fa fa-lightbulb-o mr-1"></i>
                                <strong>Note:</strong> While email is disabled, all alert emails are logged to the
                                database and can be viewed in the "Email Logs" section below.
                            </small>
                        </Alert>
                    )}
                </CardBody>
            </Card>
        );
    }
}

export default EmailConfigPanel;
